import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CabeceraPagina } from '../../components/Armazon';
import { Aviso, Boton, Campo, Cargando, Dialogo, PildoraLibre, Tarjeta, Vacio } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAvisos, useDatos } from '../../lib/sesion';

interface Comercial {
  id: string; externalId: string; name: string; email: string | null; active: boolean;
  clientCount: number; linkCount: number; userName: string | null; userId: string | null;
  userEmail: string | null; userActive: boolean | null; userLastLoginAt: string | null;
  userMustChangePassword: boolean | null; userCount: number;
}
interface Formulario { id?: string; externalId: string; name: string; email: string; active: boolean; linkCount: number }
interface Pendiente { id: string; externalId: string; name: string; email: string; reason?: string }
interface PlanPendientes { eligible: Pendiente[]; skipped: Pendiente[] }

function estadoAcceso(row: Comercial) {
  if (row.userCount > 1) return { texto: `${row.userCount} accesos · revisar en Usuarios`, tono: 'espera' as const };
  if (!row.userId) return { texto: 'Sin acceso', tono: 'neutro' as const };
  if (!row.userActive) return { texto: 'Desactivado', tono: 'alto' as const };
  if (!row.userLastLoginAt) return { texto: 'Pendiente de primer acceso', tono: 'espera' as const };
  return { texto: 'Activo', tono: 'hecho' as const };
}

export function Comerciales() {
  const [params, setParams] = useSearchParams();
  const precarga = params.get('externalId');
  const [form, setForm] = useState<Formulario | null>(precarga
    ? { externalId: precarga, name: '', email: '', active: true, linkCount: 0 } : null);
  const [eliminar, setEliminar] = useState<Comercial | null>(null);
  const [acceso, setAcceso] = useState<Comercial | null>(null);
  const [plan, setPlan] = useState<PlanPendientes | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [fallo, setFallo] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [claveTemporal, setClaveTemporal] = useState<{ nombre: string; valor: string } | null>(null);
  const { avisar } = useAvisos();
  const datos = useDatos((signal) => api.get<{ rows: Comercial[] }>('/salespeople', signal), []);
  const rows = (datos.datos?.rows ?? []).filter((r) =>
    `${r.externalId} ${r.name} ${r.email ?? ''}`.toLocaleLowerCase('es').includes(busqueda.toLocaleLowerCase('es')));

  function cerrar() {
    setForm(null); setEliminar(null); setAcceso(null); setPlan(null); setFallo(null);
    if (precarga) setParams({});
  }
  async function ejecutar(action: () => Promise<unknown>, message: string) {
    if (guardando) return;
    setGuardando(true); setFallo(null);
    try { const result = await action() as { warning?: string }; cerrar(); datos.recargar(); avisar(result?.warning ?? message, result?.warning ? 'info' : 'hecho'); }
    catch (error) { setFallo(error instanceof ApiError ? error.message : 'No se pudo completar la operación.'); }
    finally { setGuardando(false); }
  }
  function guardar() {
    if (!form) return;
    const input = { externalId: form.externalId, name: form.name, email: form.email.trim() || null, active: form.active };
    void ejecutar(() => form.id ? api.patch(`/salespeople/${form.id}`, input) : api.post('/salespeople', input), 'Comercial guardado.');
  }
  async function abrirAcceso(row: Comercial) {
    setFallo(null);
    try {
      const actuales = await api.get<{ rows: Comercial[] }>('/salespeople');
      setAcceso(actuales.rows.find((r) => r.id === row.id) ?? row);
    } catch (error) {
      setFallo(error instanceof ApiError ? error.message : 'No se pudo recargar el acceso.');
    }
  }

  return <>
    <CabeceraPagina titulo="Comerciales" />
    {fallo && !form && !eliminar && !acceso && !plan && <Aviso tono="error">{fallo}</Aviso>}
    {datos.error && <Aviso tono="error">{datos.error}</Aviso>}
    <Tarjeta titulo="Comerciales" extra={<div className="fila">
      <Boton onClick={async () => {
        setFallo(null);
        try { setPlan(await api.get<PlanPendientes>('/salespeople/accesses/pending')); }
        catch (e) { setFallo(e instanceof ApiError ? e.message : 'No se pudo preparar la lista.'); }
      }}>Crear accesos pendientes</Boton>
      <Boton variante="clave" onClick={() => {
        setFallo(null); setForm({ externalId: '', name: '', email: '', active: true, linkCount: 0 });
      }}>Nuevo comercial</Boton>
    </div>}>
      <Campo etiqueta="Buscar por Id., nombre o correo"><input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} /></Campo>
      {datos.cargando ? <Cargando /> : !rows.length ? <Vacio titulo="No hay comerciales con esta búsqueda" /> :
        <div style={{ overflowX: 'auto' }}><table className="tabla">
          <thead><tr><th>Id. Vendedor</th><th>Nombre</th><th>Correo</th><th>Clientes</th><th>Acceso</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>{rows.map((r) => { const status = estadoAcceso(r); return <tr key={r.id}>
            <td className="codigo">{r.externalId}</td><td>{r.name}</td><td>{r.email ?? '—'}</td><td>{r.clientCount}</td>
            <td><PildoraLibre texto={status.texto} tono={status.tono} /></td><td>{r.active ? 'Activo' : 'Inactivo'}</td>
            <td><div className="fila">
              <Boton tam="s" onClick={() => { setFallo(null); setForm({ ...r, email: r.email ?? '' }); }}>Editar</Boton>
              {r.userCount > 1 ? <Link to="/admin/usuarios">Revisar accesos en Usuarios</Link> : <Boton tam="s" variante={r.userId ? 'secundario' : 'clave'} onClick={() => void abrirAcceso(r)}>
                {r.userId ? 'Gestionar acceso' : 'Crear acceso'}
              </Boton>}
              {r.linkCount === 0 ? <Boton tam="s" variante="riesgo" onClick={() => { setFallo(null); setEliminar(r); }}>Eliminar</Boton>
                : <Boton tam="s" disabled={guardando} onClick={() => void ejecutar(() => api.patch(`/salespeople/${r.id}`, { active: !r.active }), r.active ? 'Comercial desactivado.' : 'Comercial activado.')}>
                  {r.active ? 'Desactivar' : 'Activar'}</Boton>}
            </div></td>
          </tr>; })}</tbody>
        </table></div>}
    </Tarjeta>

    {form && <Dialogo titulo={form.id ? 'Editar comercial' : 'Nuevo comercial'} onCerrar={() => { if (!guardando) cerrar(); }} acciones={<>
      <Boton disabled={guardando} onClick={cerrar}>Cancelar</Boton><Boton variante="clave" cargando={guardando} disabled={guardando || !form.name.trim() || !form.externalId.trim()} onClick={guardar}>Guardar</Boton>
    </>}><div className="pila">
      {fallo && <Aviso tono="error">{fallo}</Aviso>}
      <Campo etiqueta="Id. Vendedor" ayuda={form.linkCount > 0 ? 'No se puede cambiar: tiene clientes, usuarios o solicitudes asociados.' : 'Se conservan los ceros a la izquierda.'}>
        <input type="text" value={form.externalId} disabled={guardando || form.linkCount > 0} onChange={(e) => setForm({ ...form, externalId: e.target.value })} />
      </Campo>
      <Campo etiqueta="Nombre"><input type="text" value={form.name} disabled={guardando} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Campo>
      <Campo etiqueta="Correo"><input type="email" value={form.email} disabled={guardando} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Campo>
      <label className="casilla"><input type="checkbox" checked={form.active} disabled={guardando} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Activo</label>
    </div></Dialogo>}

    {acceso && <GestionAcceso comercial={acceso} guardando={guardando} fallo={fallo} onCerrar={cerrar}
      onTemporal={(valor) => { setClaveTemporal({ nombre: acceso.name, valor }); setAcceso(null); datos.recargar(); }}
      ejecutar={ejecutar} />}

    {plan && <AccesosPendientes plan={plan} guardando={guardando} fallo={fallo} onCerrar={cerrar}
      onConfirmar={(sendInvitations) => ejecutar(() => api.post('/salespeople/accesses/pending', { sendInvitations }),
        `Accesos creados${sendInvitations ? ' e invitaciones encoladas' : ''}.`)} />}

    {claveTemporal && <Dialogo titulo="Contraseña temporal" descripcion={`Solo se muestra esta vez para ${claveTemporal.nombre}.`}
      onCerrar={() => setClaveTemporal(null)} acciones={<><Boton onClick={() => navigator.clipboard.writeText(claveTemporal.valor)}>Copiar</Boton>
        <Boton variante="clave" onClick={() => setClaveTemporal(null)}>Cerrar</Boton></>}>
      <Campo etiqueta="Contraseña temporal"><input type="text" readOnly value={claveTemporal.valor} /></Campo>
    </Dialogo>}

    {eliminar && <Dialogo titulo="Eliminar comercial" descripcion={`¿Eliminar ${eliminar.externalId} · ${eliminar.name}? Solo se permite si sigue sin clientes, usuarios ni solicitudes asociados.`}
      onCerrar={() => { if (!guardando) cerrar(); }} acciones={<>
        <Boton disabled={guardando} onClick={cerrar}>Cancelar</Boton><Boton variante="riesgo" cargando={guardando} onClick={() => void ejecutar(() => api.del(`/salespeople/${eliminar.id}`), 'Comercial eliminado.')}>Eliminar</Boton>
      </>}>{fallo && <Aviso tono="error">{fallo}</Aviso>}</Dialogo>}
  </>;
}

function GestionAcceso({ comercial, guardando, fallo, onCerrar, onTemporal, ejecutar }: {
  comercial: Comercial; guardando: boolean; fallo: string | null; onCerrar: () => void;
  onTemporal: (value: string) => void;
  ejecutar: (action: () => Promise<unknown>, message: string) => Promise<void>;
}) {
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [mode, setMode] = useState<'MANUAL' | 'TEMPORARY' | 'INVITE'>('INVITE');
  if (!comercial.userId) return <Dialogo titulo="Crear acceso" descripcion={`${comercial.externalId} · ${comercial.name}. El perfil e Id. Vendedor quedan fijados como Comercial.`}
    onCerrar={onCerrar} acciones={<><Boton onClick={onCerrar}>Cancelar</Boton><Boton variante="clave" cargando={guardando}
      disabled={!comercial.email || (mode === 'MANUAL' && password.length < 10)} onClick={() => ejecutar(async () => {
          const r = await api.post<{ temporaryPassword?: string }>(`/salespeople/${comercial.id}/access`, {
            email: comercial.email, mode, ...(mode === 'MANUAL' ? { password, mustChangePassword: mustChange } : {}),
          });
          if (r.temporaryPassword) onTemporal(r.temporaryPassword);
        }, mode === 'INVITE' ? 'Acceso creado e invitación encolada.' : 'Acceso creado.')}>
        Crear acceso
      </Boton></>}>
    <div className="pila">{fallo && <Aviso tono="error">{fallo}</Aviso>}
      <Campo etiqueta="Correo"><input type="email" readOnly value={comercial.email ?? ''} /></Campo>
      {!comercial.email && <Aviso tono="aviso">Indique primero el correo en la ficha del comercial.</Aviso>}
      <Campo etiqueta="Forma de acceso"><select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
        <option value="INVITE">Enviar invitación por correo</option><option value="TEMPORARY">Generar contraseña temporal</option><option value="MANUAL">Escribir contraseña inicial</option>
      </select></Campo>
      {mode === 'MANUAL' && <><Campo etiqueta="Contraseña inicial" ayuda="Al menos 10 caracteres."><input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Campo>
        <label className="casilla"><input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} /> Obligar a cambiarla en el próximo acceso</label></>}
    </div>
  </Dialogo>;

  return <Dialogo titulo="Gestionar acceso" descripcion={`${comercial.externalId} · ${comercial.name}`}
    onCerrar={onCerrar} acciones={<><Boton onClick={onCerrar}>Cerrar</Boton><Boton variante="clave" cargando={guardando}
      onClick={() => ejecutar(() => api.patch(`/admin/users/${comercial.userId}`, {
        ...(password ? { password, mustChangePassword: mustChange } : {}),
      }), 'Acceso actualizado.')}>Guardar cambios</Boton></>}>
    <div className="pila">{fallo && <Aviso tono="error">{fallo}</Aviso>}
      <Campo etiqueta="Correo" ayuda="Se cambia en la ficha del comercial."><input type="email" readOnly value={comercial.email ?? comercial.userEmail ?? ''} /></Campo>
      <Campo etiqueta="Nueva contraseña (opcional)" ayuda="Al menos 10 caracteres."><input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Campo>
      {password && <label className="casilla"><input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} /> Obligar a cambiarla en el próximo acceso</label>}
      <div className="fila">
        <Boton disabled={!comercial.userActive} onClick={() => ejecutar(() => api.post(`/admin/users/${comercial.userId}/send-access`, {}), 'Enlace de acceso encolado.')}>Enviar acceso por correo</Boton>
        <Boton onClick={async () => { const r = await api.post<{ temporaryPassword: string }>(`/admin/users/${comercial.userId}/reset-password`, {}); onTemporal(r.temporaryPassword); }}>Restablecer temporal</Boton>
        <Boton onClick={() => ejecutar(() => api.post(`/admin/users/${comercial.userId}/active`, { active: !comercial.userActive }), comercial.userActive ? 'Acceso desactivado.' : 'Acceso activado.')}>
          {comercial.userActive ? 'Desactivar acceso' : 'Activar acceso'}
        </Boton>
      </div>
    </div>
  </Dialogo>;
}

function AccesosPendientes({ plan, guardando, fallo, onCerrar, onConfirmar }: {
  plan: PlanPendientes; guardando: boolean; fallo: string | null; onCerrar: () => void;
  onConfirmar: (send: boolean) => Promise<void>;
}) {
  const [send, setSend] = useState(true);
  return <Dialogo titulo="Crear accesos pendientes" descripcion="Revise la lista antes de confirmar."
    onCerrar={onCerrar} acciones={<><Boton onClick={onCerrar}>Cancelar</Boton><Boton variante="clave" cargando={guardando}
      disabled={!plan.eligible.length} onClick={() => onConfirmar(send)}>Crear {plan.eligible.length} accesos</Boton></>}>
    <div className="pila">{fallo && <Aviso tono="error">{fallo}</Aviso>}
      <label className="casilla"><input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} /> Enviar invitación por correo a cada uno</label>
      <h3>Se crearán ({plan.eligible.length})</h3>
      {plan.eligible.length ? <ul>{plan.eligible.map((r) => <li key={r.id}>{r.externalId} · {r.name} · {r.email}</li>)}</ul> : <p>No hay accesos válidos pendientes.</p>}
      {!!plan.skipped.length && <><h3>Se omitirán ({plan.skipped.length})</h3><ul>{plan.skipped.map((r) => <li key={r.id}>{r.externalId} · {r.email}: {r.reason}</li>)}</ul></>}
    </div>
  </Dialogo>;
}
